import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User,
  AuthError as FirebaseAuthError, // Import Firebase AuthError type
} from 'firebase/auth';
import { auth } from './firebase';
import i18n from '../i18n'; // Import the configured i18n instance directly

// Define the structure for thrown errors (code + translated message)
export interface AuthError {
  code: string;
  message: string;
}

// Helper function to get translated auth error message based on code
const getTranslatedAuthError = (error: any): AuthError => {
  const code = error.code || 'auth/unknown-error';
  let messageKey = `auth.errors.${code}`; // Default key pattern

  // Map specific known codes to potentially different keys if needed
  // Example: if 'auth/user-not-found' needs a very specific message
  // switch (code) {
  //   case 'auth/user-not-found':
  //     messageKey = 'auth.errors.userNotFoundSpecific'; // Example override
  //     break;
  //   // Add more cases as needed
  // }

  // Use i18n.t to translate, provide fallback text from original error or generic message
  const translatedMessage = i18n.t(
    messageKey,
    error.message || t('auth.errors.unknown'),
    { ns: 'translation' }
  ); // Specify namespace if needed

  return {
    code: code,
    message: translatedMessage,
  };
};

// Register function using translated errors
export const registerUser = async (
  email: string,
  password: string
): Promise<User> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    return userCredential.user;
  } catch (error: any) {
    console.error('Registration error:', error);
    // Throw the error with translated message
    throw getTranslatedAuthError(error);
  }
};

// Sign in function using translated errors
export const signIn = async (
  email: string,
  password: string
): Promise<User> => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    return userCredential.user;
  } catch (error: any) {
    console.error('Sign in error:', error);
    // Throw the error with translated message
    throw getTranslatedAuthError(error);
  }
};

// Sign out function using translated errors
export const signOut = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth);
  } catch (error: any) {
    console.error('Sign out error:', error);
    // Throw the error with translated message
    throw getTranslatedAuthError(error);
  }
};

// Subscribe function remains the same - it doesn't handle errors directly
export const subscribeToAuthChanges = (
  callback: (user: User | null) => void
): (() => void) => {
  return onAuthStateChanged(auth, callback);
};
